// createElement is mandatory for 3rd party integration, so please don't remove it.
// eslint-disable-next-line
import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import axios from 'axios';
import {
    displayProcedureContentVersion,
    getIsAssetLoaded,
    getPanelVis,
    getProcedureContentVersion,
    setAssetType,
    setLinkDisplayVis,
    setPanelVis,
    setProcedureSavedView,
} from '../../features/globals/visibilitySlice';
import {
    initAsset,
    selectActiveAsset,
    setSelectedSearchItemId,
    setAssetDetails,
    setAssetPublishedDate,
    setDependencies,
    setIsDraft,
    setZoneMedia,
} from '../../features/asset/assetSlice';
import { setupXapi, setECTLUrl } from 'GeminiViewerComponent/features/xAPI/xapiSlice';
import Bowser from 'bowser';
import Marzipano from 'marzipano';
import { loadScript, loadLink } from '../../helpers/helper';
import { setViewerLoaded } from 'GeminiViewerComponent/features/globals/viewerStatusSlice';
import {
    setActiveTheme,
    setLoadingSvg,
    setUserThemes,
    setAssetThemes,
    setDefaultAssetTheme,
} from 'GeminiViewerComponent/features/globals/themeSlice';
import { loadAsset, loadChecklist } from 'GeminiViewerComponent/features/globals/visibilitySlice';

import { setProcedureState, setInitialState, gotoNode, setCurrentNode } from '../Procedure/features/procedureSlice';
import { AssetTypes, LinkTypeIds, mapCompanionContents, prefixUrl, trackPublishedJsonLoaded } from 'GeminiViewerComponent/helpers';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';
import { clearActiveZones } from 'GeminiViewerComponent/features/zonePreview/zonePreviewSlice';
import { selectContentPanelItem } from 'GeminiViewerComponent/features/contentPanel/contentPanelItemSlice';
import { loadContentPanelItem } from 'shared/loadContentPanelSlice';
import { setActiveAssetId, setActiveAssetProcedureId } from '../Procedure/features/checklistDataSlice';
import { accountsSlice } from 'app/store';
import {
    setInspectionActionList,
    setInspectionReasonList,
} from 'GeminiViewerComponent/features/inspection/inspectionSlice';
import { setCompanionApps } from 'GeminiViewerComponent/features/companionApps/companionAppsSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';

const { setActiveUserS3Prefix, setAssetInfo } = accountsSlice;

const MarzipanoLoader = ({ onLoaded, onError }) => {
    const dispatch = useDispatch();
    const store = useStore();

    const [loadedConfig, setLoadedConfig] = useState(false);
    const config = useSelector(getConfig);
    const isPanelVis = useSelector(getPanelVis);
    const panelItem = useSelector(selectContentPanelItem);
    const isAssetLoaded = useSelector(getIsAssetLoaded);
    const procedureContentVersion = useSelector(getProcedureContentVersion);
    const asset = useSelector(selectActiveAsset);

    const isPanelVisRef = useRef(isPanelVis);
    const panelItemRef = useRef(panelItem);
    const procedureContentVersionRef = useRef(procedureContentVersion);
    const publishedRequestInFlightRef = useRef('');
    const loadedPublishedFileRef = useRef('');

    var loadingPrefix = config.modeType === 'author' ? '/' : '';

    useEffect(() => {
        isPanelVisRef.current = isPanelVis;
        panelItemRef.current = panelItem;
        procedureContentVersionRef.current = procedureContentVersion;
    }, [isPanelVis, panelItem, procedureContentVersion]);

    window.bowser = Bowser.getParser(window.navigator.userAgent);
    window.Marzipano = Marzipano;

    const saveProcedureViewerState = (zoneId) => {
        const state = store.getState();

        try {
            const viewParams = window?.geminiViewManager?.getCurrentViewParams?.();
            dispatch(
                setProcedureSavedView({
                    assetId: asset?.asset_id ?? null,
                    zoneId: zoneId ?? null,
                    viewParams: viewParams ?? null,
                    assetStoreState: state.asset,
                    procedureStoreState: state.procedure,
                })
            );
        } catch (e) {
            dispatch(
                setProcedureSavedView({
                    assetId: asset?.asset_id ?? null,
                    zoneId: zoneId ?? null,
                    viewParams: null,
                    assetStoreState: state.asset,
                    procedureStoreState: state.procedure,
                })
            );
        }
    };

    useEffect(() => {
        const publishedFile = config.paths.PUBLISHED_FILE;

        if (!loadedConfig && config.paths.PUBLISHED_FILE && isAssetLoaded !== true) {
            if (
                publishedRequestInFlightRef.current === publishedFile ||
                loadedPublishedFileRef.current === publishedFile
            ) {
                return;
            }

            publishedRequestInFlightRef.current = publishedFile;

            axios
                .get(publishedFile + '?t=' + new Date().valueOf().toString())
                .then(async function (response) {
                    if (!response.data.client) {
                        // This can happen when trying to load
                        // a local published.json file that
                        // doesn't exist
                        if (onError) {
                            onError();
                        }
                        publishedRequestInFlightRef.current = '';
                        return;
                    }

                    trackPublishedJsonLoaded(response.data);
                    loadedPublishedFileRef.current = publishedFile;
                    publishedRequestInFlightRef.current = '';

                    setLoadedConfig(true);

                    let dependencies = {};

                    // LRS
                    const lrsCreds = {
                        endpoint: response.data.lrs_endpoint,
                        client_key: response.data.lrs_client_key,
                        client_secret: response.data.lrs_client_secret,
                    };
                    const gAsset = {
                        id: response.data.asset_id,
                        type_id: response.data.asset_type_id,
                        name: response.data.display_name,
                    };

                    const gClient = {
                        id: response.data.client.client_id,
                        name: response.data.client.display_name,
                    };
                    dispatch(setAssetInfo({ clientId: response.data.client.client_id }));

                    await dispatch(
                        setupXapi({
                            LrsCredentials: lrsCreds,
                            GeminiAsset: gAsset,
                            GeminiClient: gClient,
                        })
                    );
                    await dispatch(
                        setECTLUrl({
                            ectlURL: response.data.ectl_api_url,
                            ectlToken: response.data.ectl_api_token,
                        })
                    );

                    // Asset
                    dispatch(setActiveAssetId(response.data.asset_id));
                    dispatch(setAssetType(response.data.asset_type_id));
                    dispatch(setAssetPublishedDate(response.data.published_date));
                    dispatch(setIsDraft(response.data.is_draft));
                    dispatch(setZoneMedia(response.data.zone_media));
                    dispatch(
                        setAssetDetails({
                            display_name: response.data.display_name,
                            description: response.data.description,
                        })
                    );
                    if (response.data.dependencies) {
                        let result = await dispatch(setDependencies(response.data.dependencies));
                        dependencies = result.payload;
                        if (
                            response.data.dependencies.inspection_action_lists &&
                            response.data.dependencies.inspection_reason_lists
                        ) {
                            dispatch(setInspectionActionList(response.data.dependencies.inspection_action_lists));
                            dispatch(setInspectionReasonList(response.data.dependencies.inspection_reason_lists));
                        }
                        delete response.data.dependencies;
                    } else {
                        dispatch(setDependencies({}));
                    }
                    if (response.data?.asset_type_id === AssetTypes.Procedure) {
                        dispatch(clearActiveZones());
                        dispatch(
                            initAsset({
                                ...response.data,
                                isPublishedAsset: true,
                            })
                        );
                        dispatch(setActiveUserS3Prefix(response.data.s3_prefix));
                        if (response.data?.procedure_json) {
                            if (response.data.s3_prefix && response.data?.loading_svg_uri) {
                                dispatch(
                                    setLoadingSvg({
                                        loadingSvgUri: `${response.data.s3_prefix}${response.data.loading_svg_uri}`,
                                        loadingSvgCssAnimate: response.data.loading_svg_css_animate ?? true,
                                    })
                                );
                            }

                            if (response.data.client.themes_json) {
                                dispatch(setUserThemes(JSON.parse(response.data.client.themes_json)));
                                dispatch(setAssetThemes(JSON.parse(response.data.client.themes_json)));
                                dispatch(setActiveTheme(response.data.client.active_theme ?? 'default'));
                            }

                            if (response.data.client.active_theme) {
                                dispatch(setActiveTheme(response.data.client.active_theme));
                                dispatch(setDefaultAssetTheme(response.data.client.active_theme));
                            }

                            let currentDate = new Date();
                            const offset = currentDate.getTimezoneOffset();
                            currentDate = new Date(currentDate.getTime() - offset * 60 * 1000);

                            var checkList = JSON.parse(response.data?.procedure_json);
                            checkList.header_image_url = prefixUrl(
                                response.data?.header_image_url,
                                response.data?.s3_prefix
                            );

                            if (checkList.checklist_user_data) {
                                checkList.checklist_user_data.date = currentDate.toISOString().split('T')[0];
                            }

                            dispatch(setActiveAssetProcedureId(checkList?.procedure_id));
                            await dispatch(
                                setProcedureState({
                                    ...checkList,
                                    dependencies: dependencies,
                                    clearHistory: true,
                                })
                            );
                            dispatch(gotoNode(Number(checkList?.start_node_id) ? Number(checkList.start_node_id) : 1));
                            dispatch(loadChecklist(true));
                        }
                        dispatch(setViewerLoaded());
                        if (onLoaded) {
                            onLoaded();
                        }
                    } else {
                        dispatch(setInitialState());
                        dispatch(clearActiveZones());
                        dispatch(
                            initAsset({
                                ...response.data,
                                isPublishedAsset: true,
                            })
                        );
                        dispatch(setActiveUserS3Prefix(response.data.s3_prefix));
                        if (response.data.s3_prefix && response.data?.loading_svg_uri) {
                            dispatch(
                                setLoadingSvg({
                                    loadingSvgUri: `${response.data.s3_prefix}${response.data.loading_svg_uri}`,
                                    loadingSvgCssAnimate: response.data.loading_svg_css_animate ?? true,
                                })
                            );
                        }

                        if (response.data.client.themes_json) {
                            dispatch(setUserThemes(JSON.parse(response.data.client.themes_json)));
                            dispatch(setAssetThemes(JSON.parse(response.data.client.themes_json)));
                            dispatch(setActiveTheme(response.data.client.active_theme ?? 'default'));
                        }

                        if (response.data.client.active_theme) {
                            dispatch(setActiveTheme(response.data.client.active_theme));
                            dispatch(setDefaultAssetTheme(response.data.client.active_theme));
                        }
                        if (response.data.asset_companion_apps) {
                            dispatch(
                                setCompanionApps(
                                    mapCompanionContents(response.data.asset_companion_apps, response.data.s3_prefix)
                                )
                            );
                        }

                        // loadLink(`${loadingPrefix}reset.min.css`, undefined, 'reset-css');
                        dispatch(setViewerLoaded());
                    }
                    dispatch(loadAsset(true));
                    if (onLoaded) {
                        onLoaded();
                    }
                })
                .catch(function (error) {
                    publishedRequestInFlightRef.current = '';
                    console.log(error);
                    if (onError) {
                        onError(error);
                    }
                });
        } else {
            // loadLink(`${loadingPrefix}reset.min.css`, undefined, 'reset-css');
            dispatch(setViewerLoaded());
            if (onLoaded) {
                onLoaded();
            }
        }
    }, [config, onLoaded, onError]); // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        window.showHotspot = (hotspotId) => {
            if (!hotspotId) {
                return;
            }
            var sceneAndHotspot = findSceneWithHotspot(hotspotId);

            if (sceneAndHotspot != null) {
                var scene = sceneAndHotspot.scene;
                window.viewerManager.viewer.switchScene(scene);
                var hotspot = sceneAndHotspot.hotspot;
                if (hotspot.yaw || hotspot.x) {
                    scene.lookTo({
                        yaw: hotspot.yaw,
                        pitch: hotspot.pitch,
                        x: hotspot.x,
                        y: hotspot.y,
                    });
                }
                window.handleHotspot(hotspot.zone_id, hotspot.item_id);
            } else {
                console.log('MarzipanoViewer: Hotspot undefined');
            }
        };

        // Define handleHotspot function
        window.handleHotspot = async (zoneId, hotspotId, fromSearch = false) => {
            if (
                isPanelVisRef.current &&
                panelItemRef.current.item_id === hotspotId &&
                !procedureContentVersionRef.current
            ) {
                // Close panel if user clicks on same item again
                dispatch(setPanelVis({ open: false }));
            } else {
                const item = await dispatch(
                    loadContentPanelItem({
                        zoneId: zoneId,
                        itemId: hotspotId,
                    })
                ).unwrap();
                // If there is only  one content item then show
                // it immediately.
                // const item = store.getState().asset.panelItem;
                if (
                    item?.links.length === 1 &&
                    (item.auto_view_single_link === true || asset?.auto_view_single_link === true)
                ) {
                    const link = item.links[0];
                    if (link.link_type.link_type_id === LinkTypeIds.Procedure) {
                        saveProcedureViewerState(zoneId);
                        await dispatch(activateLoading());
                        await dispatch(setPanelVis({ open: false }));
                        await dispatch(setSelectedSearchItemId(hotspotId));
                        await dispatch(displayProcedureContentVersion(link.content_version));
                        const startNodeId = Number(link?.content_version?.procedure_start_node);
                        if (!Number.isNaN(startNodeId)) {
                            await dispatch(setCurrentNode(startNodeId));
                        }
                        await dispatch(deactivateLoading());
                    } else {
                        dispatch(
                            setLinkDisplayVis({
                                open: true,
                                linkContent:
                                    link.content_version?.encoding_status &&
                                        link.content_version?.encoding_status.toLowerCase() === 'complete'
                                        ? link?.content_version?.encoded_url
                                        : link.content_version.url
                                            ? link.content_version.url
                                            : link.content_version.embed_data,
                                linkData: {
                                    ...link,
                                    ...link.content,
                                    ...(typeof link.content_version === 'object' ? link.content_version : {}),
                                },
                                linkTypeId: link.link_type.link_type_id,
                                autoPlay: Boolean(link?.auto_play),
                                overlayText: link.content_version?.overlay_text,
                            })
                        );
                    }
                } else if (!fromSearch || item?.links.length > 0) {
                    const hideEmptyPanel = !!(asset?.options?.hide_empty_item_panel ?? asset?.hide_empty_item_panel);
                    if (!hideEmptyPanel) {
                        dispatch(setPanelVis({ open: true }));
                    } else if (item?.links.length > 0) {
                        dispatch(setPanelVis({ open: true }));
                    } else {
                        dispatch(setPanelVis({ open: false }));
                    }
                }
            }
        };
        // eslint-disable-next-line
    }, [asset]);

    function findSceneWithHotspot(hotspotId) {
        var result = null;

        window.viewerManager.scenes.forEach(function (scene) {
            if (result != null) {
                return;
            }
            scene.hotspots?.forEach(function (hotspot) {
                if (hotspot.item_id === hotspotId) {
                    result = { scene: scene.scene, hotspot: hotspot };
                    return;
                }
            });
        });

        return result;
    }

    return null;
};

export { MarzipanoLoader };
